import { adoptUserLanguage } from "../i18n";
import api, { clearTokens, REFRESH_KEY, storeTokens, TOKEN_KEY, USER_KEY } from "./api";
import { currentPushToken, resetPush, unregisterPush } from "./push";

/** Sent on every sign-in request so the login history says where it came from. */
export const LOGIN_PLATFORM = "web";

/**
 * POST /business/login. With 2FA off this stores the JWT and loads the profile;
 * with 2FA on the API withholds the token and answers with a challenge token
 * instead, which the caller passes to the challenge screen.
 *
 * @returns {{ user: object }|{ twoFactorRequired: true, challengeToken: string, expiresIn: number }}
 */
export const login = async (email, password) => {
  const { data } = await api.post("/business/login", { email, password, platform: LOGIN_PLATFORM });

  if (data.two_factor_required) {
    // Deliberately not persisted: this half-authenticated state should die
    // with the tab, not survive in localStorage.
    return {
      twoFactorRequired: true,
      challengeToken: data.challenge_token,
      expiresIn: data.expires_in,
    };
  }

  storeTokens(data);

  const user = await fetchMe();
  adoptUserLanguage(user);
  return { user };
};

/**
 * POST /business/2fa/challenge — the second step of a 2FA login. Send either a
 * 6-digit `code` or a `recoveryCode`; a valid one buys the real JWT.
 */
export const verifyTwoFactorChallenge = async (challengeToken, { code, recoveryCode }) => {
  const payload = { challenge_token: challengeToken, platform: LOGIN_PLATFORM };
  if (recoveryCode) payload.recovery_code = recoveryCode.trim();
  else payload.code = code;

  const { data } = await api.post("/business/2fa/challenge", payload);
  storeTokens(data);

  const user = await fetchMe();
  adoptUserLanguage(user);
  return { user, recoveryCodesRemaining: data.recovery_codes_remaining };
};

/** GET /business/me */
export const fetchMe = async () => {
  const { data } = await api.get("/business/me");
  const user = data.data.business_user;
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
};

/** POST /business/logout */
/** PATCH /business/me — remember the user's own language for their next login. */
export const saveMyLanguage = async (language) => {
  const { data } = await api.patch("/business/me", { preferred_language: language });
  const user = data.data.business_user;
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
};

// Logging out keeps the UI language: only the session goes.
export const logout = async () => {
  const deviceToken = currentPushToken();
  try {
    // The refresh token and this browser's push token go too, so the server
    // revokes the one and deletes the other.
    await api.post("/business/logout", {
      refresh_token: localStorage.getItem(REFRESH_KEY),
      ...(deviceToken ? { device_token: deviceToken } : {}),
    });
  } catch (_) {
    // The token may already be expired; clearing it locally is what matters.
    // The separate unregister call is the fallback for the push token.
    if (deviceToken) await unregisterPush();
  }
  clearTokens();
  await resetPush();
};

export const isAuthenticated = () => !!localStorage.getItem(TOKEN_KEY);

export const getCurrentUser = () => {
  const raw = localStorage.getItem(USER_KEY);
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
